import { useEffect, useRef, useState } from 'react';

export default function Testimonials() {
  const [isVisible, setIsVisible] = useState(false);
  const [hoveredCard, setHoveredCard] = useState<string | null>(null);
  const [animatedWords, setAnimatedWords] = useState<Record<string, number>>({});
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Word-by-word animation effect
  useEffect(() => {
    if (!hoveredCard) {
      setAnimatedWords({});
      return;
    }

    const testimonial = testimonials.find(t => t.name === hoveredCard);
    if (!testimonial) return;

    const words = testimonial.text.split(' ');
    let currentIndex = 0;

    const interval = setInterval(() => {
      if (currentIndex < words.length) {
        setAnimatedWords(prev => ({
          ...prev,
          [hoveredCard]: currentIndex + 1
        }));
        currentIndex++;
      } else {
        clearInterval(interval);
      }
    }, 180);

    return () => clearInterval(interval);
  }, [hoveredCard]);

  const testimonials = [
    {
      name: 'Scarlett Lee',
      role: 'Visual Designer | UX/UI Designer | Strategist',
      image: 'https://public.readdy.ai/ai/img_res/16d890fe71ab21fd6d612f7c413ac29e.jpg',
      text: 'Working with Charit has been a pleasure. He consistently puts in his best effort and excels in communication, ensuring that we achieve great design outcomes together. I believe he would be a positive addition to any team.',
      rating: 5,
      date: 'December 24, 2024',
      relationship: 'Scarlett managed Charit directly',
    },
    {
      name: 'Rahul Baskaran',
      role: 'Ex-Design Lead Bigbasket & Censa',
      image: 'https://readdy.ai/api/search-image?query=Professional%20man%20business%20executive%20smiling%20confident%20portrait%20corporate%20headshot%20clean%20white%20background%20professional%20attire%20friendly%20approachable%20indian&width=200&height=200&seq=testimonial-rahul&orientation=squarish',
      text: 'Charit is knowledgeable, articulate, and a pleasure to work with. He brings integrity to his work, and I believe his overall presence positively impacted the project he worked on.',
      rating: 5,
      date: 'October 7, 2021',
      relationship: 'Rahul worked with Charit on the same team',
    },
  ];

  const renderAnimatedText = (text: string, cardName: string) => {
    const words = text.split(' ');
    const visibleWordCount = animatedWords[cardName] || 0;

    return (
      <p className="text-gray-900 text-base leading-relaxed italic break-words">
        "{words.map((word, index) => (
          <span
            key={index}
            className={`inline transition-opacity duration-300 ${
              index < visibleWordCount ? 'opacity-100' : 'opacity-0'
            }`}
            style={{ marginRight: '0.25rem' }}
          >
            {word}
          </span>
        ))}"
      </p>
    );
  };

  return (
    <section
      id="testimonials"
      ref={sectionRef}
      className="relative py-32 bg-white"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Section Header */}
        <div className={`text-center mb-20 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-10'}`}>
          <span className="inline-block text-sm font-semibold text-slate-500 uppercase tracking-wider mb-4">Client Feedback</span>
          <h2 className="text-5xl lg:text-6xl font-bold text-slate-900 mb-6">
            What Clients Say
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            Trusted by teams and leaders across various industries
          </p>
        </div>

        {/* Testimonials Grid */}
        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {testimonials.map((testimonial, index) => (
            <div
              key={testimonial.name}
              className={`group bg-white border-2 border-slate-200 rounded-2xl p-8 hover:shadow-xl transition-all duration-700 cursor-pointer overflow-hidden ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-20'
              } ${hoveredCard === testimonial.name ? '' : 'h-48'}`}
              style={{ transitionDelay: `${index * 0.2}s` }}
              onMouseEnter={(e) => {
                setHoveredCard(testimonial.name);
                e.currentTarget.style.borderColor = '#6b3d52';
              }}
              onMouseLeave={(e) => {
                setHoveredCard(null);
                e.currentTarget.style.borderColor = '#e2e8f0';
              }}
            >
              {/* Author - Initially centered, moves down on hover */}
              <div className={`flex items-center gap-4 transition-all duration-500 ${
                hoveredCard === testimonial.name ? 'mb-6' : 'justify-center h-full'
              }`}>
                <div className="relative flex-shrink-0">
                  <div className={`w-16 h-16 rounded-full overflow-hidden border-4 transition-all duration-300`}
                       style={{ borderColor: hoveredCard === testimonial.name ? '#6b3d52' : '#cbd5e1' }}>
                    <img
                      src={testimonial.image}
                      alt={testimonial.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  {/* Speaking animation */}
                  <div className="absolute -bottom-1 -right-1 w-6 h-6 flex items-center justify-center rounded-full border-2 border-white" style={{ backgroundColor: '#6b3d52' }}>
                    <div className="flex gap-0.5 items-end">
                      <div className="w-0.5 bg-white rounded-full animate-sound-wave-1" style={{ height: '8px' }}></div>
                      <div className="w-0.5 bg-white rounded-full animate-sound-wave-2" style={{ height: '12px' }}></div>
                      <div className="w-0.5 bg-white rounded-full animate-sound-wave-3" style={{ height: '8px' }}></div>
                    </div>
                  </div>
                </div>
                <div className="min-w-0">
                  <h4 className="font-bold text-slate-900 text-lg truncate">{testimonial.name}</h4>
                  <p className={`text-sm text-slate-600 transition-all duration-500 ${
                    hoveredCard === testimonial.name ? 'opacity-100 max-h-20' : 'opacity-0 max-h-0 overflow-hidden'
                  }`}>
                    {testimonial.role}
                  </p>
                </div>
              </div>

              {/* Content - Only visible on hover */}
              <div className={`transition-all duration-500 ${
                hoveredCard === testimonial.name ? 'opacity-100' : 'opacity-0 max-h-0 overflow-hidden'
              }`}>
                {/* Stars */}
                <div className="flex gap-1 mb-6">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-xl" style={{ color: '#fbbf24' }}></i>
                  ))}
                </div>

                {/* Quote with word-by-word animation */}
                <div className="mb-6">
                  {renderAnimatedText(testimonial.text, testimonial.name)}
                </div>

                {/* Date and Relationship */}
                <p className="text-sm text-slate-500 break-words">
                  {testimonial.date} • {testimonial.relationship}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
